// Shared create + edit form for the dealer-org-admin Locations
// section. Renders all editable columns of `public.locations` except
// the social_* group (which has its own admin path).
//
// `onSubmit` / `onCancel` / `busy` come from the section so the same
// form can drive both the inline create path and the per-row edit
// path. The form does NOT own `is_active` — soft-delete / reactivate
// is exposed as a row-level toggle in OrgLocationsList instead.
//
// Slice 13a — online-only / corporate UX:
//   • When location_type ∈ {virtual, corporate} and the org has an
//     active corporate HQ with a street address, render a
//     "Use corporate HQ address" prefill button. Per the workflow doc
//     §11, virtual / corporate rows still need an address anchor for
//     order routing.
//   • In CREATE mode only, switching the type dropdown TO virtual or
//     corporate auto-flips is_public → false (online-only sales
//     locations and back-office HQs shouldn't appear on customer-
//     facing dealer-finder maps by default). The user can re-check
//     Public after the auto-flip if they actually want it surfaced.
//     EDIT mode never auto-modifies is_public — that respects the
//     existing row's setting.
import { LOCATION_TYPE_VALUES, type OrgLocation } from "@/lib/orgLocations/orgLocationsTypes";
import type { OrgLocationFormDraft } from "./useOrgLocationsTab";
import { OrgLocationsFormOnlineHint } from "./OrgLocationsFormOnlineHint";
import { StructuredAddressFields } from "@/components/common/StructuredAddressFields";

const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";

const labelClass = "block text-xs text-gray-600 dark:text-zinc-300";

const primaryBtn =
  "rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";
const ghostBtn =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700";

type Props = {
  mode: "create" | "edit";
  form: OrgLocationFormDraft;
  setForm: (next: OrgLocationFormDraft) => void;
  onSubmit: () => void | Promise<void>;
  onCancel: () => void;
  busy: boolean;
  /** Slice 13a: optional corporate-HQ anchor for one-click address
   *  prefill on virtual / corporate rows. The host computes this from
   *  the org's existing locations and excludes the row being edited
   *  (so we never offer to prefill a row from itself). */
  corporateAnchor?: OrgLocation | null;
};

const ONLINE_LIKE_TYPES = new Set(["virtual", "corporate"]);

function isOnlineLike(t: OrgLocationFormDraft["location_type"]): boolean {
  return ONLINE_LIKE_TYPES.has(t);
}

export function OrgLocationsForm({
  mode,
  form,
  setForm,
  onSubmit,
  onCancel,
  busy,
  corporateAnchor,
}: Props) {
  const setField = <K extends keyof OrgLocationFormDraft>(key: K, value: OrgLocationFormDraft[K]) => {
    setForm({ ...form, [key]: value });
  };

  function handleTypeChange(nextType: OrgLocationFormDraft["location_type"]) {
    // Slice 13a smart-default: create mode only — flip is_public off
    // when the user picks virtual/corporate. We don't restore it when
    // they pick a non-online-like type later (their second choice is
    // their choice; we don't reverse it).
    const shouldAutoUnPublic =
      mode === "create" && isOnlineLike(nextType) && !isOnlineLike(form.location_type) && form.is_public;
    if (shouldAutoUnPublic) {
      setForm({ ...form, location_type: nextType, is_public: false });
    } else {
      setForm({ ...form, location_type: nextType });
    }
  }

  function prefillFromCorporateHQ() {
    if (!corporateAnchor) return;
    setForm({
      ...form,
      address_1: corporateAnchor.address_1 ?? "",
      address_2: corporateAnchor.address_2 ?? "",
      city: corporateAnchor.city ?? "",
      state: corporateAnchor.state ?? "",
      postal_code: corporateAnchor.postal_code ?? "",
      country: corporateAnchor.country ?? "US",
    });
  }

  const onlineLikeHint = isOnlineLike(form.location_type);

  return (
    <form
      className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-3 dark:border-indigo-900/50 dark:bg-indigo-950/20"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className={labelClass}>
          Name *
          <input
            className={inputClass}
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="e.g. Ravenel Retail Lot"
            required
            disabled={busy}
          />
        </label>
        <label className={labelClass}>
          Location code
          <input
            className={inputClass}
            value={form.location_code}
            onChange={(e) => setField("location_code", e.target.value)}
            placeholder="e.g. ravenel  (unique within your org)"
            disabled={busy}
          />
        </label>
        <label className={labelClass}>
          Type
          <select
            className={inputClass}
            value={form.location_type}
            onChange={(e) =>
              handleTypeChange(e.target.value as OrgLocationFormDraft["location_type"])
            }
            disabled={busy}
          >
            <option value="">(unspecified)</option>
            {LOCATION_TYPE_VALUES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-200">
            <input
              type="checkbox"
              checked={form.is_public}
              onChange={(e) => setField("is_public", e.target.checked)}
              disabled={busy}
            />
            Public (appears on dealer-finder maps and customer-facing surfaces)
          </label>
        </div>
      </div>

      {onlineLikeHint && (form.location_type === "virtual" || form.location_type === "corporate") ? (
        <OrgLocationsFormOnlineHint
          type={form.location_type}
          anchor={corporateAnchor ?? null}
          onPrefill={prefillFromCorporateHQ}
          busy={busy}
        />
      ) : null}

      <StructuredAddressFields
        value={{
          street: form.address_1,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
        }}
        onChange={(next) =>
          setForm({
            ...form,
            address_1: next.street ?? "",
            city: next.city ?? "",
            state: next.state ?? "",
            postal_code: next.postal_code ?? "",
          })
        }
        disabled={busy}
        inputClassName={inputClass}
        labelClassName={labelClass}
      />

      <label className={labelClass}>
        Address line 2
        <input
          className={inputClass}
          value={form.address_2}
          onChange={(e) => setField("address_2", e.target.value)}
          placeholder="Suite, unit, etc. (optional)"
          disabled={busy}
        />
      </label>

      <label className={labelClass}>
        Country
        <input
          className={inputClass}
          value={form.country}
          onChange={(e) => setField("country", e.target.value)}
          placeholder="US"
          maxLength={8}
          disabled={busy}
        />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className={labelClass}>
          Phone
          <input
            className={inputClass}
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="8431234567"
            disabled={busy}
          />
        </label>
        <label className={labelClass}>
          Email
          <input
            className={inputClass}
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="store-mgr@example.com"
            type="email"
            disabled={busy}
          />
        </label>
      </div>

      <label className={labelClass}>
        Notes
        <textarea
          className={`${inputClass} min-h-[60px]`}
          value={form.notes}
          onChange={(e) => setField("notes", e.target.value)}
          placeholder="Internal notes about this location (not customer-facing)"
          disabled={busy}
        />
      </label>

      <div className="flex items-center gap-2">
        <button type="submit" className={primaryBtn} disabled={busy}>
          {busy ? "Saving…" : mode === "create" ? "Create location" : "Save changes"}
        </button>
        <button type="button" className={ghostBtn} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
